/**
 * Client Communication Hub - World Class Minimalist Design
 * Ultra-clean, elegant messaging inspired by Linear & Apple
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Send, Loader2, MessageSquare, Sparkles, RefreshCw, UserPlus, 
  Check, CheckCheck, Paperclip, Mail, MessageCircle,
  Search, Bot, Inbox
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { auth } from '../../../utils/auth';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { format, isToday, isYesterday } from 'date-fns';
import InviteClientModal from '../../portal/InviteClientModal';
import type { Message, ClientData, CaseData } from './types';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface ClientCommunicationHubProps {
  caseId: string;
  caseData: CaseData | null;
  client: ClientData | null;
  documents?: any[];
}

const CHANNELS = {
  PORTAL: { icon: MessageCircle, label: 'Portal' },
  EMAIL: { icon: Mail, label: 'E-post' },
  SMS: { icon: MessageSquare, label: 'SMS' },
  OTHER: { icon: MessageSquare, label: 'Annet' }
};

interface Thread {
  id: string;
  subject: string;
  channel: keyof typeof CHANNELS;
  messages: Message[];
  lastMessage: Message;
  unreadCount: number;
  needsResponse: boolean;
  participants: string[];
}

const getMessageChannel = (msg: Message): keyof typeof CHANNELS => {
  if (msg.source === 'CLIENT_PORTAL' || msg.channel === 'PORTAL') return 'PORTAL';
  if (msg.channel === 'email' || msg.source === 'outlook' || msg.outlook_message_id) return 'EMAIL';
  if (msg.channel === 'sms') return 'SMS';
  return 'OTHER';
};

const formatDateLabel = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isToday(date)) return 'I dag';
  if (isYesterday(date)) return 'I går';
  return format(date, 'd. MMM');
};

const groupIntoThreads = (messages: Message[]): Thread[] => {
  const threadMap = new Map<string, Message[]>();
  
  messages.forEach(msg => {
    const threadKey = msg.conversation_id || msg.conversationId || 
                      msg.subject?.toLowerCase().replace(/^re:\s*/i, '').trim() ||
                      `thread-${msg.case_id}`;
    if (!threadMap.has(threadKey)) threadMap.set(threadKey, []);
    threadMap.get(threadKey)!.push(msg);
  });
  
  return Array.from(threadMap.entries()).map(([id, msgs]) => {
    const sortedMsgs = msgs.sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
    const lastMsg = sortedMsgs[sortedMsgs.length - 1];
    const firstMsg = sortedMsgs[0];
    const needsResponse = lastMsg.direction === 'INCOMING' || lastMsg.direction === 'INBOUND';
    const participants = [...new Set(msgs.map(m => m.sender_name || (m.from as any)?.name || 'Unknown'))];
    
    return { id, subject: firstMsg.subject || 'Samtale', channel: getMessageChannel(firstMsg), messages: sortedMsgs, lastMessage: lastMsg, unreadCount: msgs.filter(m => !m.is_read && (m.direction === 'INCOMING' || m.direction === 'INBOUND')).length, needsResponse, participants };
  }).sort((a, b) => new Date(b.lastMessage.created_at || 0).getTime() - new Date(a.lastMessage.created_at || 0).getTime());
};

const ClientCommunicationHub: React.FC<ClientCommunicationHubProps> = ({ caseId, caseData, client, documents }) => {
  const { i18n } = useTranslation('caseWorkspace');
  const [communications, setCommunications] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [generatingReply, setGeneratingReply] = useState(false);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [showAiSummary, setShowAiSummary] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'needs-response'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  const currentUser = auth.getUser();
  const isEnglish = i18n.language === 'en';
  const lawyerProfilePic = currentUser?.profile_picture_url || currentUser?.profilePicture || null;
  
  const fetchCommunications = useCallback(async () => {
    if (!caseId) return;
    try {
      const token = auth.getToken();
      const [commsResponse, emailsResponse] = await Promise.all([
        axios.get(`${API}/cases/${caseId}/communications`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { communications: [] } })),
        axios.get(`${API}/cases/${caseId}/emails`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { emails: [] } }))
      ]);
      
      const comms = commsResponse.data.communications || [];
      const emails = emailsResponse.data.emails || [];
      
      const transformedEmails = emails.map((email: any) => ({
        id: email.id, case_id: caseId, channel: 'email', direction: email.direction || 'INCOMING',
        subject: email.subject, body: email.body_preview || email.body, body_preview: email.body_preview,
        sender_email: email.from?.email, sender_name: email.from?.name, created_at: email.created_at || email.received_at,
        attachments: email.attachments || [], source: 'outlook', outlook_message_id: email.outlook_message_id,
        conversation_id: email.conversationId || email.conversation_id
      }));
      
      const allMessages = [...comms, ...transformedEmails].sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
      setCommunications(allMessages);
      
      if (!selectedThreadId && allMessages.length > 0) {
        const threads = groupIntoThreads(allMessages);
        if (threads.length > 0) setSelectedThreadId(threads[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  }, [caseId, selectedThreadId]);

  useEffect(() => {
    fetchCommunications();
    const poll = setInterval(fetchCommunications, 5000);
    return () => clearInterval(poll);
  }, [fetchCommunications]);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [communications.length, selectedThreadId]);
  
  const threads = useMemo(() => groupIntoThreads(communications), [communications]);
  const filteredThreads = useMemo(() => {
    let result = threads;
    if (filter === 'needs-response') result = result.filter(t => t.needsResponse);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => t.subject.toLowerCase().includes(q) || t.messages.some(m => m.body?.toLowerCase().includes(q)));
    }
    return result;
  }, [threads, filter, searchQuery]);
  
  const selectedThread = useMemo(() => threads.find(t => t.id === selectedThreadId) || null, [threads, selectedThreadId]);
  const stats = useMemo(() => ({ total: threads.length, needsResponse: threads.filter(t => t.needsResponse).length }), [threads]);
  
  const generateAISummary = async () => {
    if (!selectedThread) return;
    setGeneratingSummary(true);
    try {
      const token = auth.getToken();
      const text = selectedThread.messages.map(m => `${m.sender_name}: ${m.body || ''}`).join('\n');
      const res = await axios.post(`${API}/ai/summarize`, { text, type: 'conversation', language: isEnglish ? 'en' : 'no' }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.summary) { setAiSummary(res.data.summary); setShowAiSummary(true); }
    } catch {
      setAiSummary(`${selectedThread.messages.length} meldinger. ${selectedThread.needsResponse ? 'Venter på ditt svar.' : ''}`);
      setShowAiSummary(true);
    } finally {
      setGeneratingSummary(false);
    }
  };
  
  const generateAIReply = async () => {
    if (!selectedThread) return;
    setGeneratingReply(true);
    try {
      const token = auth.getToken();
      const lastClientMsg = [...selectedThread.messages].reverse().find(m => m.direction === 'INCOMING' || m.direction === 'INBOUND');
      const res = await axios.post(`${API}/communications/ai/generate-reply`, { case_id: caseId, thread_subject: selectedThread.subject, last_message: lastClientMsg?.body || '', tone: 'professional' }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.reply) { setNewMessage(res.data.reply); inputRef.current?.focus(); toast.success('AI-utkast klar'); }
    } catch { toast.error('Kunne ikke generere'); }
    finally { setGeneratingReply(false); }
  };
  
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedThread) return;
    const text = newMessage;
    setNewMessage('');
    setSending(true);
    const lastMsg = selectedThread.messages[selectedThread.messages.length - 1];
    try {
      const token = auth.getToken();
      if (lastMsg?.id) {
        try {
          await axios.post(`${API}/communications/${lastMsg.id}/reply`, { reply_body: text }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (commErr: any) {
          if (commErr?.response?.status === 404) {
            if (lastMsg?.outlook_message_id) {
              await axios.post(`${API}/outlook/reply`, { message_id: lastMsg.outlook_message_id, body: text, is_html: false, reply_all: false }, { headers: { Authorization: `Bearer ${token}` } });
            } else {
              await axios.post(`${API}/inbox/messages/${lastMsg.id}/reply`, { reply_body: text }, { headers: { Authorization: `Bearer ${token}` } });
            }
          } else {
            throw commErr;
          }
        }
      }
      toast.success('Sendt!');
      fetchCommunications();
    } catch { toast.error('Feilet'); setNewMessage(text); }
    finally { setSending(false); }
  };

  const groupedMessages = useMemo(() => {
    if (!selectedThread) return {};
    const groups: Record<string, Message[]> = {};
    selectedThread.messages.forEach(msg => {
      const key = msg.created_at ? format(new Date(msg.created_at), 'yyyy-MM-dd') : 'unknown';
      if (!groups[key]) groups[key] = [];
      groups[key].push(msg);
    });
    return groups;
  }, [selectedThread]);
  
  if (loading) return (
    <div className="h-full flex items-center justify-center bg-white">
      <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
    </div>
  );

  return (
    <div data-testid="messages-hub" className="h-full flex bg-white" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter", system-ui, sans-serif' }}>
      
      {/* Left Panel - Thread List */}
      <div className="w-[340px] bg-white border-r border-gray-100/80 flex flex-col">
        
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-gray-900 tracking-tight">Meldinger</h2>
            <button 
              onClick={() => setShowInviteModal(true)} 
              data-testid="invite-client-btn"
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all"
            >
              <UserPlus className="w-4 h-4" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk i meldinger..."
              data-testid="search-messages"
              className="w-full h-10 pl-10 pr-4 bg-gray-50 rounded-xl text-sm placeholder:text-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-gray-100 transition-all"
            />
          </div>
        </div>
        
        {/* Filter Pills */}
        <div className="px-6 pb-4 flex gap-2">
          <button
            onClick={() => setFilter('all')}
            data-testid="filter-all"
            className={cn(
              "px-4 py-2 rounded-full text-xs font-medium transition-all",
              filter === 'all' 
                ? "bg-gray-900 text-white" 
                : "bg-gray-50 text-gray-500 hover:bg-gray-100"
            )}
          >
            Alle ({stats.total})
          </button>
          <button
            onClick={() => setFilter('needs-response')}
            data-testid="filter-needs-response"
            className={cn(
              "px-4 py-2 rounded-full text-xs font-medium transition-all flex items-center gap-2",
              filter === 'needs-response' 
                ? "bg-gray-900 text-white" 
                : "bg-gray-50 text-gray-500 hover:bg-gray-100"
            )}
          >
            {stats.needsResponse > 0 && <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />}
            Venter svar ({stats.needsResponse})
          </button>
        </div>
        
        {/* Thread List */}
        <div className="flex-1 overflow-y-auto">
          {filteredThreads.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-8">
              <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mb-4">
                <Inbox className="w-7 h-7 text-gray-300" />
              </div>
              <p className="text-sm text-gray-400">Ingen samtaler ennå</p>
            </div>
          ) : (
            <div className="px-3">
              {filteredThreads.map(thread => {
                const isSelected = selectedThreadId === thread.id;
                const ChannelIcon = CHANNELS[thread.channel].icon;
                
                return (
                  <button
                    key={thread.id}
                    onClick={() => setSelectedThreadId(thread.id)}
                    data-testid={`thread-${thread.id}`}
                    className={cn(
                      "w-full px-4 py-4 text-left rounded-xl transition-all mb-1",
                      isSelected 
                        ? "bg-gray-900" 
                        : "hover:bg-gray-50"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className={cn(
                        "w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium",
                        isSelected ? "bg-white/10 text-white" : "bg-gray-100 text-gray-600"
                      )}>
                        {(thread.participants[0] || 'K').charAt(0).toUpperCase()}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className={cn(
                            "text-sm font-medium truncate",
                            isSelected ? "text-white" : thread.unreadCount > 0 ? "text-gray-900" : "text-gray-700"
                          )}>
                            {thread.participants[0] || 'Klient'}
                          </span>
                          <span className={cn(
                            "text-[11px] flex-shrink-0 ml-2",
                            isSelected ? "text-white/50" : "text-gray-400"
                          )}>
                            {thread.lastMessage.created_at ? format(new Date(thread.lastMessage.created_at), 'd. MMM') : ''}
                          </span>
                        </div>
                        
                        <p className={cn(
                          "text-xs truncate leading-relaxed",
                          isSelected ? "text-white/60" : "text-gray-400"
                        )}>
                          {thread.lastMessage.body?.slice(0, 60) || 'Ingen innhold'}
                        </p>
                        
                        {/* Status indicators */}
                        <div className="flex items-center gap-2 mt-2">
                          <span className={cn(
                            "flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full",
                            isSelected ? "bg-white/10 text-white/70" : "bg-gray-100 text-gray-500"
                          )}>
                            <ChannelIcon className="w-3 h-3" />
                            {CHANNELS[thread.channel].label}
                          </span>
                          
                          {thread.needsResponse && (
                            <span className={cn(
                              "flex items-center gap-1 text-[10px] font-medium",
                              isSelected ? "text-white/60" : "text-gray-500"
                            )}>
                              <span className={cn("w-1.5 h-1.5 rounded-full", isSelected ? "bg-white/60" : "bg-gray-400")} />
                              Venter
                            </span>
                          )}
                          
                          {thread.unreadCount > 0 && !isSelected && (
                            <span className="px-1.5 py-0.5 bg-indigo-600 text-white rounded-full text-[10px] font-semibold">
                              {thread.unreadCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {/* Right Panel - Conversation */}
      <div className="flex-1 flex flex-col bg-white">
        {selectedThread ? (
          <>
            {/* Conversation Header */}
            <div className="px-8 py-5 border-b border-gray-100/80">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center shadow-sm">
                    <span className="text-white font-medium text-base">
                      {(selectedThread.participants[0] || 'K').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-base">{selectedThread.participants[0] || 'Klient'}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {selectedThread.messages.length} meldinger · {CHANNELS[selectedThread.channel].label}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  <button 
                    onClick={generateAISummary} 
                    disabled={generatingSummary}
                    data-testid="ai-summary-btn"
                    className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-all"
                  >
                    {generatingSummary ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bot className="w-3.5 h-3.5" />}
                    Oppsummering
                  </button>
                  <button 
                    onClick={fetchCommunications}
                    className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* AI Summary */}
              <AnimatePresence>
                {showAiSummary && aiSummary && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }} 
                    animate={{ opacity: 1, height: 'auto' }} 
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4"
                  >
                    <div className="p-4 bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl border border-indigo-100/50">
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                        </div>
                        <p className="text-sm text-gray-700 flex-1 leading-relaxed">{aiSummary}</p>
                        <button onClick={() => setShowAiSummary(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-8 py-6 bg-white">
              <div className="max-w-2xl mx-auto space-y-6">
                {Object.entries(groupedMessages).map(([date, msgs]) => (
                  <div key={date}>
                    {/* Date Separator */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="flex-1 h-px bg-gray-100" />
                      <span className="text-[11px] text-gray-400 font-medium">
                        {formatDateLabel(date)}
                      </span>
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>
                    
                    {/* Messages */}
                    <div className="space-y-3">
                      {msgs.map((msg, idx) => {
                        const isOutgoing = msg.direction === 'OUTGOING' || msg.direction === 'OUTBOUND';
                        const prevMsg = msgs[idx - 1];
                        const nextMsg = msgs[idx + 1];
                        const showAvatar = !prevMsg || prevMsg.direction !== msg.direction;
                        const isLast = !nextMsg || nextMsg.direction !== msg.direction;
                        const fromObj = msg.from as Record<string, unknown> | undefined;
                        const senderName = msg.sender_name || (fromObj?.name as string) || 'Klient';
                        
                        return (
                          <motion.div
                            key={msg.id || idx}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2 }}
                            className={cn("flex gap-3", isOutgoing ? "flex-row-reverse" : "flex-row")}
                          >
                            {/* Avatar */}
                            {showAvatar ? (
                              isOutgoing && lawyerProfilePic ? (
                                <img src={lawyerProfilePic} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 shadow-sm" />
                              ) : (
                                <div className={cn(
                                  "w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-medium shadow-sm",
                                  isOutgoing ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-700"
                                )}>
                                  {isOutgoing ? (currentUser?.name?.charAt(0) || 'D') : senderName.charAt(0)}
                                </div>
                              )
                            ) : (
                              <div className="w-9" />
                            )}
                            
                            {/* Message Bubble */}
                            <div className={cn("max-w-[70%]", isOutgoing ? "items-end" : "items-start")}>
                              {showAvatar && (
                                <p className={cn(
                                  "text-[11px] font-medium text-gray-500 mb-1.5 px-1",
                                  isOutgoing ? "text-right" : "text-left"
                                )}>
                                  {isOutgoing ? (currentUser?.name || 'Du') : senderName}
                                </p>
                              )}
                              
                              {(msg.body || msg.body_preview || (msg.attachments && msg.attachments.length > 0)) && (
                                <div className={cn(
                                  "px-4 py-3 text-sm leading-relaxed shadow-sm",
                                  isOutgoing 
                                    ? "bg-gray-900 text-white rounded-2xl rounded-tr-lg" 
                                    : "bg-white text-gray-800 rounded-2xl rounded-tl-lg border border-gray-100"
                                )}>
                                  {(msg.body || msg.body_preview) && (
                                    <p className="whitespace-pre-wrap">{msg.body || msg.body_preview}</p>
                                  )}
                                  
                                  {msg.attachments && msg.attachments.length > 0 && (
                                    <div className={cn(
                                      "mt-2 pt-2 border-t flex flex-wrap gap-2",
                                      isOutgoing ? "border-white/10" : "border-gray-100",
                                      !(msg.body || msg.body_preview) && "mt-0 pt-0 border-t-0"
                                    )}>
                                      {msg.attachments.map((att: any, i: number) => (
                                        <div key={i} className={cn(
                                          "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs",
                                          isOutgoing ? "bg-white/10" : "bg-gray-50"
                                        )}>
                                          <Paperclip className="w-3 h-3" />
                                          <span className="truncate max-w-[100px]">{att.filename || att.name || 'Fil'}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {isLast && (
                                <div className={cn("flex items-center gap-1.5 mt-1.5 px-1", isOutgoing ? "justify-end" : "justify-start")}>
                                  <span className="text-[10px] text-gray-400">
                                    {msg.created_at ? format(new Date(msg.created_at), 'HH:mm') : ''}
                                  </span>
                                  {isOutgoing && (
                                    <span className={msg.client_read ? "text-indigo-500" : "text-gray-300"}>
                                      {msg.client_read ? <CheckCheck className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>
            
            {/* Compose Area */}
            <div className="bg-white border-t border-gray-100/80 px-8 py-5">
              <div className="max-w-2xl mx-auto">
                {/* Needs Response Notice */}
                {selectedThread.needsResponse && (
                  <div className="flex items-center gap-2.5 px-4 py-2.5 bg-gray-50 rounded-xl mb-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    <span className="text-xs text-gray-500 font-medium">Klienten venter på ditt svar</span>
                  </div>
                )}
                
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <textarea
                      ref={inputRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Skriv en melding..."
                      data-testid="message-input"
                      rows={1}
                      className="w-full px-5 py-3.5 bg-gray-50 rounded-2xl text-sm placeholder:text-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-gray-100 transition-all resize-none border border-transparent focus:border-gray-200"
                      style={{ minHeight: '52px', maxHeight: '140px' }}
                      onInput={(e) => {
                        const t = e.target as HTMLTextAreaElement;
                        t.style.height = 'auto';
                        t.style.height = Math.min(t.scrollHeight, 140) + 'px';
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                    />
                    
                    <div className="flex items-center justify-between mt-2 px-2">
                      <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all">
                        <Paperclip className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={generateAIReply} 
                        disabled={generatingReply}
                        data-testid="ai-draft-btn"
                        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                      >
                        {generatingReply ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        AI-utkast
                      </button>
                    </div>
                  </div>
                  
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sending}
                    data-testid="send-message-btn"
                    className="h-12 w-12 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-100 text-white disabled:text-gray-400 rounded-xl flex items-center justify-center transition-all shadow-sm hover:shadow"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-white">
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-gray-400 text-sm">Velg en samtale for å begynne</p>
            </div>
          </div>
        )}
      </div>
      
      <InviteClientModal caseId={caseId} isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} prefillData={{ name: client?.name || caseData?.client_name, email: client?.email }} />
    </div>
  );
};

export default ClientCommunicationHub;
